import { useEffect, useRef } from 'react';

/**
 * Map of key combos to handlers. Combos: "k", "escape", "mod+k" (Ctrl/Cmd).
 * Plain-key shortcuts are ignored while typing in form fields.
 */
export type ShortcutMap = Record<string, () => void>;

export function useKeyboardShortcuts(shortcuts: ShortcutMap): void {
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping =
        !!target &&
        (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable);

      const combo = `${event.ctrlKey || event.metaKey ? 'mod+' : ''}${event.key.toLowerCase()}`;
      const handler = shortcutsRef.current[combo];
      if (!handler) return;
      if (isTyping && !combo.startsWith('mod+') && combo !== 'escape') return;

      event.preventDefault();
      handler();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
